import React, { useMemo } from 'react'
import { ChartPluginComponentProps } from '../../../_types'
import { convertSecondsToString } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/time'
import { TFunction } from 'i18next'
import { EMPTY_VALUE_TEXT } from '../../../_constants/text'

type Props = ChartPluginComponentProps<'direction' | 'running' | 'M28Running'>

const _formatDurationSeconds = (seconds: number, t: TFunction): string => {
  if (seconds === 0) {
    return EMPTY_VALUE_TEXT
  }
  return convertSecondsToString(seconds, t)
}

/**
 * Calculates the total number of seconds within [intervalStart, intervalEnd]
 * that overlap with any of the provided pause intervals.
 *
 * @param intervalStart - The start of the interval.
 * @param intervalEnd - The end of the interval.
 * @param pauses - An array of pause periods, each with a start and end time.
 * @returns The total number of seconds overlapping with pause periods.
 *
 * Example:
 *   interval: 100 - 200
 *   pauses: [{start: 120, end: 130}, {start: 140, end: 160}]
 *   → returns (130-120) + (160-140) = 10 + 20 = 30
 */
const _getPauseOverlapSeconds = (
  intervalStart: number,
  intervalEnd: number,
  pauses: Array<{ start: number; end: number }>
): number => {
  let overlapSeconds = 0

  for (const pause of pauses) {
    const overlapStart = Math.max(intervalStart, pause.start)
    const overlapEnd = Math.min(intervalEnd, pause.end)
    if (overlapEnd > overlapStart) {
      overlapSeconds += overlapEnd - overlapStart
    }
  }

  return overlapSeconds
}

function BackwardForwardTimeSummary(props: Props) {
  const { data, t, workShift } = props

  const { forwardSeconds, backwardSeconds } = useMemo(() => {
    if (!data?.length) {
      return { forwardSeconds: 0, backwardSeconds: 0 }
    }

    if (data.length < 2) {
      return { forwardSeconds: 0, backwardSeconds: 0 }
    }

    let _forwardSeconds = 0
    let _backwardSeconds = 0

    const pausesInRange =
      workShift?.pauses
        ?.map(pause => ({ start: Number(pause.start), end: Number(pause.end) }))
        .filter(pause => Number.isFinite(pause.start) && Number.isFinite(pause.end) && pause.end > pause.start) ?? []

    // Each sample `i` represents the interval [time[i], time[i+1]].
    // Count forward/backward intervals only when machine is running and outside pauses.
    for (let i = 0; i < data.length - 1; i++) {
      const current = data[i]
      const next = data[i + 1]

      const seconds = next.time - current.time
      if (seconds <= 0) {
        continue
      }

      const isRunning = current.running === 1 && current.M28Running === 1
      if (!isRunning) {
        continue
      }

      const pauseOverlap = _getPauseOverlapSeconds(current.time, next.time, pausesInRange)
      const effectiveSeconds = Math.max(0, seconds - pauseOverlap)
      if (effectiveSeconds <= 0) {
        continue
      }

      const direction = current.direction
      if (direction === 1) {
        _forwardSeconds += effectiveSeconds
      } else if (direction === 0) {
        _backwardSeconds += effectiveSeconds
      }
    }

    return { forwardSeconds: _forwardSeconds, backwardSeconds: _backwardSeconds }
  }, [data, workShift])

  return (
    <div className={'row row-legend row-legend-bottom'}>
      <div className="col-md-10">
        <h5>{t('plantAnalysis.backwardForwardTimeSummary.title')}</h5>
        <table className="table table-striped table-sm">
          <thead>
            <tr>
              <th>{t('plantAnalysis.backwardForwardTimeSummary.direction.title')}</th>
              <th>{t('plantAnalysis.backwardForwardTimeSummary.time.title')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('plantAnalysis.backwardForwardTimeSummary.forward.title')}</td>
              <td>{_formatDurationSeconds(forwardSeconds, t)}</td>
            </tr>
            <tr>
              <td>{t('plantAnalysis.backwardForwardTimeSummary.backward.title')}</td>
              <td>{_formatDurationSeconds(backwardSeconds, t)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default BackwardForwardTimeSummary
